// Firebase Auth for the community beta.
//
// Visitors are signed in anonymously in the background, so they can browse
// (Firestore rules require request.auth != null). When they hit an action that
// needs an account, the AuthModal opens. Signing up LINKS the new Google or
// email login to that anonymous account, so the uid stays the same.
//
// Console setup: Authentication → Sign-in method → enable Google,
// Email/Password and Anonymous.

import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  linkWithCredential,
  linkWithPopup,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from "firebase/auth";

import { auth } from "../firebase";

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

/**
 * Google sign-in / sign-up.
 * Anonymous visitor → link Google to their anonymous account (keeps the uid).
 * If that Google account already belongs to a member, sign in to it instead.
 */
export async function continueWithGoogle() {
  const current = auth.currentUser;

  if (current?.isAnonymous) {
    try {
      const result = await linkWithPopup(current, googleProvider);
      return { user: result.user, isNewUser: true };
    } catch (err) {
      if (err.code === "auth/credential-already-in-use") {
        const credential = GoogleAuthProvider.credentialFromError(err);
        const result = await signInWithCredential(auth, credential);
        return { user: result.user, isNewUser: false };
      }
      throw err;
    }
  }

  const result = await signInWithPopup(auth, googleProvider);
  return { user: result.user, isNewUser: Boolean(getAdditionalUserInfo(result)?.isNewUser) };
}

/** New account with email + password (linked to the anonymous session). */
export async function signUpWithEmail({ name, email, password }) {
  const current = auth.currentUser;
  let user;

  if (current?.isAnonymous) {
    const credential = EmailAuthProvider.credential(email.trim(), password);
    user = (await linkWithCredential(current, credential)).user;
  } else {
    user = (await createUserWithEmailAndPassword(auth, email.trim(), password)).user;
  }

  if (name?.trim()) await updateProfile(user, { displayName: name.trim() });
  return { user, isNewUser: true };
}

/** Existing member. Replaces the anonymous session (its uid is simply dropped). */
export async function signInWithEmail({ email, password }) {
  const result = await signInWithEmailAndPassword(auth, email.trim(), password);
  return { user: result.user, isNewUser: false };
}

export function resetPassword(email) {
  return sendPasswordResetEmail(auth, email.trim());
}

/** Sign out, then drop back to an anonymous browsing session. */
export async function signOutToGuest() {
  await signOut(auth);
};

/** Errors the user caused by closing the popup: show nothing. */
export function isSilentAuthError(err) {
  return ["auth/popup-closed-by-user", "auth/cancelled-popup-request", "auth/user-cancelled"].includes(err?.code);
}

const MESSAGES = {
  "auth/email-already-in-use": "There's already an account with that email. Try signing in instead.",
  "auth/credential-already-in-use": "That login already belongs to an account. Try signing in instead.",
  "auth/account-exists-with-different-credential": "You've signed up with that email a different way. Try email and password.",
  "auth/invalid-credential": "That email and password don't match. Check them and try again.",
  "auth/wrong-password": "That email and password don't match. Check them and try again.",
  "auth/user-not-found": "We couldn't find an account with that email.",
  "auth/invalid-email": "Enter a valid email address, like name@example.com.",
  "auth/missing-password": "Enter your password.",
  "auth/weak-password": "Use at least 6 characters for your password.",
  "auth/too-many-requests": "Too many attempts. Wait a minute, then try again.",
  "auth/popup-blocked": "Your browser blocked the Google window. Allow pop-ups for this site and try again.",
  "auth/network-request-failed": "You seem to be offline. Check your connection and try again.",
};

export function authErrorMessage(err) {
  return MESSAGES[err?.code] || "Something went wrong. Please try again.";
}
